import React, { Fragment } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { Col, Row } from 'reactstrap';
import ProfileSettingsContainer from './ProfileSettings';
import IntegrationsSettingsContainer from './IntegrationsSettings';
import CompanySettingsContainer from './CompanySettings';
import AdminsSettingsContainer from './AdminsSettings';
import EmailSettingsContainer from './EmailSettings';
import SubscriptionsSettingsContainer from './SubscriptionSettings';
import ThemedLinkStep from '../../components/themed/ThemedLinkStep';
import CompanySlack from '../companies/CompanySlack';
import CompanyAzureAd from '../companies/CompanyAzureAd';
import CompanyAzureSSO from '../companies/CompanyAzureSSO';
import CompanyGoogle from '../companies/CompanyGoogle';
import { Helmet } from 'react-helmet';
import classes from 'classnames';
import CompanyTeams from '../companies/CompanyTeams';
import { useStores } from '../../hooks/use-stores';
import APIKeys from './APIKeys';
import { Heading2 } from '@awarego-ui/components';
import RoleRoute from '../../hocs/RoleRoute';
import RequireRole from '../RequireRole';
import CompanyVanta from '../companies/CompanyVanta';
import CompanyVantaSync from '../companies/CompanyVantaSync';
import CompanyMicrosoftDefenderAst from '../companies/CompanyMicrosoftDefenderAst';
import WebHooks from './WebHooks';
import AuditLog from './AuditLog';
import { Drawer, IconButton } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';

export default ({ companyId }) => {
    const location = useLocation();
    const { store } = useStores();
    const { currentCompany, currentCompanyUISettings } = store;
    const [showMobileMenu, setShowMobileMenu] = React.useState(false);
    const [collapsed, setCollapsed] = React.useState(false);
    const [drawerOpen, setDrawerOpen] = React.useState(false);

    const collapseStorageKey = React.useMemo(() => {
        if (companyId) {
            return `settings_sidebar_collapsed_company_${companyId}`;
        }
        return 'settings_sidebar_collapsed_company_default';
    }, [companyId]);

    React.useEffect(() => {
        try {
            const stored = window.localStorage.getItem(collapseStorageKey);
            if (stored === '1') {
                setCollapsed(true);
            }
        } catch {
            // ignore storage errors
        }
    }, [collapseStorageKey]);

    const toggleCollapsed = (event) => {
        if (event && event.stopPropagation) {
            event.stopPropagation();
        }
        setCollapsed((prev) => {
            const next = !prev;
            try {
                window.localStorage.setItem(
                    collapseStorageKey,
                    next ? '1' : '0'
                );
            } catch {
                // ignore storage errors
            }
            if (!next) {
                // If unpinning, close drawer
                setDrawerOpen(false);
            }
            return next;
        });
    };

    const handleBurgerClick = () => {
        setDrawerOpen(true);
    };

    const handleDrawerClose = () => {
        setDrawerOpen(false);
    };

    const showLessonMenu = () => {
        setShowMobileMenu(true);
    };
    const hideLessonMenu = () => {
        setShowMobileMenu(false);
    };

    const currentCompanyHasSetting = (key) => {
        return (
            currentCompanyUISettings[key] &&
            currentCompanyUISettings[key] === '1'
        );
    };

    return (
        <Fragment>
            <Helmet>
                <title>Settings</title>
            </Helmet>

            {/* Fixed burger button when collapsed */}
            {collapsed && (
                <IconButton
                    onClick={handleBurgerClick}
                    sx={{
                        position: 'fixed',
                        left: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 1000,
                        backgroundColor: 'white',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        '&:hover': {
                            backgroundColor: '#f5f5f5',
                        },
                    }}
                >
                    <MenuIcon />
                </IconButton>
            )}

            {/* Drawer menu when collapsed */}
            <Drawer
                anchor="left"
                open={drawerOpen && collapsed}
                onClose={handleDrawerClose}
                PaperProps={{
                    sx: {
                        width: 280,
                        paddingTop: 2,
                    },
                }}
            >
                <div style={{ padding: '16px' }}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '16px',
                        }}
                    >
                        <Heading2 displayBlock>Settings</Heading2>
                        <IconButton
                            onClick={toggleCollapsed}
                            size="small"
                            title="Pin sidebar"
                        >
                            {collapsed ? (
                                <PushPinOutlinedIcon />
                            ) : (
                                <PushPinIcon />
                            )}
                        </IconButton>
                    </div>
                    <ThemedLinkStep
                        active={
                            location.pathname === '/settings' ||
                            location.pathname === '/settings/profile'
                                ? 'active'
                                : 0
                        }
                        to="/settings"
                        text="My profile"
                        onClick={handleDrawerClose}
                    />
                    <RequireRole>
                        <hr />
                        <ThemedLinkStep
                            active={
                                location.pathname === '/settings/company'
                                    ? 'active'
                                    : 0
                            }
                            to="company"
                            text="Company settings"
                            onClick={handleDrawerClose}
                        />
                        <ThemedLinkStep
                            active={
                                location.pathname === '/settings/subscriptions'
                                    ? 'active'
                                    : 0
                            }
                            to="subscriptions"
                            text="Subscription"
                            onClick={handleDrawerClose}
                        />
                        <ThemedLinkStep
                            active={
                                location.pathname === '/settings/admins'
                                    ? 'active'
                                    : 0
                            }
                            to="admins"
                            text="Admins"
                            onClick={handleDrawerClose}
                        />
                        <ThemedLinkStep
                            active={
                                location.pathname.startsWith('/settings/email')
                                    ? 'active'
                                    : 0
                            }
                            to="email"
                            text="Email settings"
                            onClick={handleDrawerClose}
                        />
                        <ThemedLinkStep
                            active={
                                location.pathname.startsWith(
                                    '/settings/integrations'
                                )
                                    ? 'active'
                                    : 0
                            }
                            to="integrations"
                            text="Integrations"
                            onClick={handleDrawerClose}
                        />

                        {currentCompany &&
                            !currentCompanyUISettings['ui.api_keys_hidden'] && (
                                <ThemedLinkStep
                                    active={
                                        location.pathname.startsWith(
                                            '/settings/apikeys'
                                        )
                                            ? 'active'
                                            : 0
                                    }
                                    to="apikeys"
                                    text="API Keys"
                                    onClick={handleDrawerClose}
                                />
                            )}
                        {currentCompany &&
                            !(
                                currentCompanyUISettings['ui.webhooks_hidden'] ===
                                '1'
                            ) && (
                                <ThemedLinkStep
                                    active={
                                        location.pathname.startsWith(
                                            '/settings/webhooks'
                                        )
                                            ? 'active'
                                            : 0
                                    }
                                    to="webhooks"
                                    text="Webhooks"
                                    onClick={handleDrawerClose}
                                />
                            )}
                        {currentCompany &&
                            !(
                                currentCompanyUISettings['ui.show_audit'] ===
                                '1'
                            ) && (
                                <ThemedLinkStep
                                    active={
                                        location.pathname.startsWith(
                                            '/settings/audit-log'
                                        )
                                            ? 'active'
                                            : 0
                                    }
                                    to="audit-log"
                                    text="Audit log"
                                    onClick={handleDrawerClose}
                                />
                            )}
                    </RequireRole>
                </div>
            </Drawer>

            <div className="d-flex">
                <div
                    className={classes('sidebar-mobile-menu ', {
                        show: !showMobileMenu,
                    })}
                    onClick={() => showLessonMenu()}
                ></div>
                <div
                    className={classes('sidebar-mobile-menu close', {
                        show: showMobileMenu,
                    })}
                    onClick={() => hideLessonMenu()}
                ></div>

                {!collapsed && (
                    <div
                        className={classes(
                            'block sidebar-steps sidebar sidebar-links ',
                            { open: showMobileMenu }
                        )}
                        onClick={() => hideLessonMenu()}
                    >
                        <div className="sidebar-inner">
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    marginBottom: '16px',
                                    paddingLeft: '34px',
                                    paddingRight: '16px',
                                }}
                            >
                                <Heading2
                                    mb={16}
                                    mt={showMobileMenu ? 16 : 8}
                                    displayBlock
                                >
                                    Settings
                                </Heading2>
                                <IconButton
                                    onClick={toggleCollapsed}
                                    size="small"
                                    title="Collapse sidebar"
                                >
                                    <PushPinIcon />
                                </IconButton>
                            </div>
                            <ThemedLinkStep
                                active={
                                    location.pathname === '/settings' ||
                                    location.pathname === '/settings/profile'
                                        ? 'active'
                                        : 0
                                }
                                to="/settings"
                                text="My profile"
                            />
                            <RequireRole>
                                <hr />
                                <ThemedLinkStep
                                    active={
                                        location.pathname === '/settings/company'
                                            ? 'active'
                                            : 0
                                    }
                                    to="company"
                                    text="Company settings"
                                />
                                <ThemedLinkStep
                                    active={
                                        location.pathname ===
                                        '/settings/subscriptions'
                                            ? 'active'
                                            : 0
                                    }
                                    to="subscriptions"
                                    text="Subscription"
                                />
                                <ThemedLinkStep
                                    active={
                                        location.pathname === '/settings/admins'
                                            ? 'active'
                                            : 0
                                    }
                                    to="admins"
                                    text="Admins"
                                />
                                <ThemedLinkStep
                                    active={
                                        location.pathname.startsWith(
                                            '/settings/email'
                                        )
                                            ? 'active'
                                            : 0
                                    }
                                    to="email"
                                    text="Email settings"
                                />
                                <ThemedLinkStep
                                    active={
                                        location.pathname.startsWith(
                                            '/settings/integrations'
                                        )
                                            ? 'active'
                                            : 0
                                    }
                                    to="integrations"
                                    text="Integrations"
                                />

                                {currentCompany &&
                                    !currentCompanyUISettings[
                                        'ui.api_keys_hidden'
                                    ] && (
                                        <ThemedLinkStep
                                            active={
                                                location.pathname.startsWith(
                                                    '/settings/apikeys'
                                                )
                                                    ? 'active'
                                                    : 0
                                            }
                                            to="apikeys"
                                            text="API Keys"
                                        />
                                    )}
                                {currentCompany &&
                                    !(
                                        currentCompanyUISettings[
                                            'ui.webhooks_hidden'
                                        ] === '1'
                                    ) && (
                                        <ThemedLinkStep
                                            active={
                                                location.pathname.startsWith(
                                                    '/settings/webhooks'
                                                )
                                                    ? 'active'
                                                    : 0
                                            }
                                            to="webhooks"
                                            text="Webhooks"
                                        />
                                    )}
                                {currentCompany &&
                                    !(
                                        currentCompanyUISettings['ui.show_audit'] ===
                                        '1'
                                    ) && (
                                        <ThemedLinkStep
                                            active={
                                                location.pathname.startsWith(
                                                    '/settings/audit-log'
                                                )
                                                    ? 'active'
                                                    : 0
                                            }
                                            to="audit-log"
                                            text="Audit log"
                                        />
                                    )}
                            </RequireRole>
                        </div>
                    </div>
                )}
                <div
                    className={classes('d-flex flex-column', {
                        'fullwidth': !collapsed,
                    })}
                    style={collapsed ? { width: '100%' } : {}}
                >
                    <div>
                        <Row>
                            <Col md="12" xs="12">
                                <Routes>
                                    <Route path="/">
                                        <Route
                                            index
                                            element={
                                                <ProfileSettingsContainer />
                                            }
                                        />
                                        <Route
                                            path="company"
                                            element={
                                                <RoleRoute>
                                                    <CompanySettingsContainer />
                                                </RoleRoute>
                                            }
                                        />
                                        <Route
                                            path="subscriptions"
                                            element={
                                                <RoleRoute>
                                                    <SubscriptionsSettingsContainer />
                                                </RoleRoute>
                                            }
                                        />
                                        <Route
                                            path="admins"
                                            element={
                                                <RoleRoute>
                                                    <AdminsSettingsContainer />
                                                </RoleRoute>
                                            }
                                        />
                                        <Route
                                            path="email"
                                            element={
                                                <RoleRoute>
                                                    <EmailSettingsContainer
                                                        companyId={companyId}
                                                    />
                                                </RoleRoute>
                                            }
                                        />
                                        {currentCompany &&
                                            !currentCompanyHasSetting(
                                                'ui.api_keys_hidden'
                                            ) && (
                                                <Route
                                                    path="apikeys"
                                                    element={
                                                        <RoleRoute>
                                                            <APIKeys />
                                                        </RoleRoute>
                                                    }
                                                />
                                            )}
                                        {currentCompany &&
                                            !(
                                                currentCompanyHasSetting(
                                                    'ui.webhooks_hidden'
                                                ) === '1'
                                            ) && (
                                                <Route
                                                    path="webhooks"
                                                    element={
                                                        <RoleRoute>
                                                            <WebHooks />
                                                        </RoleRoute>
                                                    }
                                                />
                                            )}
                                        <Route
                                            path="audit-log"
                                            element={
                                                <RoleRoute>
                                                    <AuditLog />
                                                </RoleRoute>
                                            }
                                        />
                                        <Route
                                            path="integrations"
                                            exact
                                            element={
                                                <RoleRoute>
                                                    <IntegrationsSettingsContainer
                                                        companyId={companyId}
                                                    />
                                                </RoleRoute>
                                            }
                                        />
                                        <Route
                                            path="integrations/slack"
                                            element={
                                                <RoleRoute>
                                                    <CompanySlack
                                                        companyId={companyId}
                                                    />
                                                </RoleRoute>
                                            }
                                        />
                                        <Route
                                            path="integrations/teams"
                                            element={
                                                <RoleRoute>
                                                    <CompanyTeams
                                                        companyId={companyId}
                                                    />
                                                </RoleRoute>
                                            }
                                        />
                                        <Route
                                            path="integrations/google"
                                            element={
                                                <RoleRoute>
                                                    <CompanyGoogle
                                                        companyId={companyId}
                                                    />
                                                </RoleRoute>
                                            }
                                        />
                                        <Route
                                            path="integrations/azure"
                                            element={
                                                <RoleRoute>
                                                    <CompanyAzureAd
                                                        companyId={companyId}
                                                    />
                                                </RoleRoute>
                                            }
                                        />
                                        <Route
                                            path="integrations/vanta"
                                            element={
                                                <RoleRoute>
                                                    <CompanyVanta
                                                        companyId={companyId}
                                                    />
                                                </RoleRoute>
                                            }
                                        />
                                        <Route
                                            path="integrations/vantasync"
                                            element={
                                                <RoleRoute>
                                                    <CompanyVantaSync
                                                        companyId={companyId}
                                                    />
                                                </RoleRoute>
                                            }
                                        />
                                        <Route
                                            path="integrations/microsoft_defender_ast"
                                            element={
                                                <RoleRoute>
                                                    <CompanyMicrosoftDefenderAst
                                                        companyId={companyId}
                                                        variant="integration"
                                                    />
                                                </RoleRoute>
                                            }
                                        />
                                        <Route
                                            path="integrations/azuresso"
                                            element={
                                                <RoleRoute>
                                                    <CompanyAzureSSO
                                                        companyId={companyId}
                                                    />
                                                </RoleRoute>
                                            }
                                        />
                                    </Route>
                                </Routes>
                            </Col>
                        </Row>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};
